import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getSystemSettings, getBusinessHours } from '../services/settings';
import { SystemSettings } from '../types/system';
import { BusinessHour, BusinessHourResponse } from '../types/businessHour';
import { isEqual } from 'lodash';
import { useAuth } from './AuthContext';

interface SystemSettingsContextType {
  settings: SystemSettings | null;
  systemSettings: SystemSettings | null;
  businessHoursSimple: {
    start: string;
    end: string;
  } | null;
  allBusinessHours: BusinessHour[] | null; // 全営業時間データを追加
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const defaultContextValue: SystemSettingsContextType = {
  settings: null,
  systemSettings: null,
  businessHoursSimple: null,
  allBusinessHours: null, // 初期値を追加
  loading: true,
  error: null,
  refreshSettings: async () => {},
};

export const SystemSettingsContext = createContext<SystemSettingsContextType>(defaultContextValue);

export const SystemSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [businessHoursSimple, setBusinessHoursSimple] = useState<{ start: string; end: string } | null>(null);
  const [allBusinessHours, setAllBusinessHours] = useState<BusinessHour[] | null>(null); // 全データを保持するstateを追加
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  
  // レート制限設定
  const MIN_REFRESH_INTERVAL = 30000; // 最小30秒間隔

  const fetchSettings = useCallback(async (force: boolean = false) => {
    const now = Date.now();
    
    // レート制限チェック（force=true の場合はスキップ。ログイン直後の再取得などで使用）
    if (!force && lastFetchTime > 0 && (now - lastFetchTime) < MIN_REFRESH_INTERVAL) {
      console.log(`SystemSettingsContext - レート制限: ${MIN_REFRESH_INTERVAL - (now - lastFetchTime)}ms 待機が必要`);
      return;
    }
    
    // 同時実行防止
    if (isRefreshing) {
      console.log('SystemSettingsContext - 既にリフレッシュ中です');
      return;
    }
    
    try {
      setIsRefreshing(true);
      setLoading(true);
      setError(null);
      setLastFetchTime(now);

      // システム設定を取得
      const settingsData = await getSystemSettings();
      if (!isEqual(settings, settingsData)) {
        setSettings(settingsData);
      }

      // 営業時間を取得
      const businessHoursResponse = await getBusinessHours();
      console.log('営業時間データ取得:', businessHoursResponse);

      // APIからの応答が配列かオブジェクトかを判定して、businessDataを正しく取得する
      const businessData: BusinessHour[] | null = Array.isArray(businessHoursResponse)
        ? businessHoursResponse
        : (
          businessHoursResponse &&
          typeof businessHoursResponse === 'object' &&
          'success'in businessHoursResponse &&
          businessHoursResponse.success &&
          'data' in businessHoursResponse &&
          Array.isArray(businessHoursResponse.data)
        )
        ? businessHoursResponse.data
        : null;

      if (businessData && businessData.length > 0) {
        console.log('SystemSettingsContext: setting allBusinessHours with data:', businessData);
        setAllBusinessHours(businessData); // 取得した全データをstateに保存
        const today = new Date().getDay(); // 0 (Sunday) - 6 (Saturday)
        const todaysHours = businessData.find(bh => bh.day_of_week === today);

        if (todaysHours) {
          const newHours = {
            start: todaysHours.start_time,
            end: todaysHours.end_time
          };
          if (!isEqual(businessHoursSimple, newHours)) {
            setBusinessHoursSimple(newHours);
          }
          console.log(`今日の営業時間（${todaysHours.day_name}）を設定: ${todaysHours.start_time}～${todaysHours.end_time}`);
        } else {
            console.warn(`今日の曜日（${today}）に対応する営業時間が見つかりませんでした。フォールバックします。`);
            // 今日のデータがない場合は、配列の最初の要素か、システム設定のトップレベルの値を使う
            const fallbackHours = businessData[0];
            if (fallbackHours) {
                 const newHours = {
                    start: fallbackHours.start_time,
                    end: fallbackHours.end_time
                };
                if (!isEqual(businessHoursSimple, newHours)) {
                  setBusinessHoursSimple(newHours);
                }
                console.log(`フォールバックとして最初の曜日の営業時間を設定: ${fallbackHours.start_time}～${fallbackHours.end_time}`);
            } else if (settingsData?.business_hours_start && settingsData?.business_hours_end) {
                const newHours = {
                    start: settingsData.business_hours_start,
                    end: settingsData.business_hours_end
                };
                if (!isEqual(businessHoursSimple, newHours)) {
                  setBusinessHoursSimple(newHours);
                }
                console.log(`フォールバック: システム設定の営業時間を使用: ${settingsData.business_hours_start}～${settingsData.business_hours_end}`);
            } else {
                 const newHours = { start: '18:00', end: '05:00' };
                 if (!isEqual(businessHoursSimple, newHours)) {
                   setBusinessHoursSimple(newHours);
                 }
                 console.log('最終フォールバック：静的デフォルト値を設定');
            }
        }
      } else if (settingsData?.business_hours_start && settingsData?.business_hours_end) {
        // フォールバックとしてシステム設定のトップレベルの値を使用
        const newHours = {
          start: settingsData.business_hours_start,
          end: settingsData.business_hours_end
        };
        if (!isEqual(businessHoursSimple, newHours)) {
          setBusinessHoursSimple(newHours);
        }
        console.log(`フォールバック: システム設定の営業時間を使用: ${settingsData.business_hours_start}～${settingsData.business_hours_end}`);
      } else {
        // 最終的なフォールバック
        console.log('営業時間データが取得できなかったため、静的デフォルト値を設定: 18:00～05:00');
        const newHours = {
          start: '18:00',
          end: '05:00'
        };
        if (!isEqual(businessHoursSimple, newHours)) {
          setBusinessHoursSimple(newHours);
        }
      }
    } catch (err: any) {
      console.error('システム設定の取得に失敗しました:', err);
      
      // 429エラーの場合は特別処理
      if (err?.response?.status === 429) {
        console.warn('SystemSettingsContext - レート制限エラー、既存データを保持');
        setError('リクエストが多すぎます。既存データを使用しています。');
        
        // 既存のbusinessHoursSimpleがなければデフォルト値を設定
        if (!businessHoursSimple) {
          console.log('429エラー - デフォルト営業時間を設定: 18:00～05:00');
          setBusinessHoursSimple({
            start: '18:00',
            end: '05:00'
          });
        }
      } else {
        setError('システム設定の取得に失敗しました');
        
        // エラー時もデフォルト値を設定
        console.log('エラーが発生したため、デフォルト値を設定: 18:00～05:00');
        const newHours = {
          start: '18:00',
          end: '05:00'
        };
        if (!isEqual(businessHoursSimple, newHours)) {
          setBusinessHoursSimple(newHours);
        }
      }
    } finally {
      console.log('SystemSettingsContext: fetchSettings finished. Setting loading to false.');
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [lastFetchTime, isRefreshing]);

  useEffect(() => {
    fetchSettings();
  }, []); // 依存配列を空にすることで、マウント時に一度だけ実行されるようにする

  // 認証が成立したら営業時間設定を強制的に再取得する。
  // アプリ起動直後(ログイン前)はトークンが無く business-hours が401になり、
  // デフォルト値で確定してしまうため、ログイン後に正しい値を取り直す。
  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings(true); // force=true でレート制限を無視して再取得
    }
    // fetchSettings は依存に入れない（再生成で無限ループを避ける）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const contextValue = {
    settings,
    systemSettings: settings,
    businessHoursSimple,
    allBusinessHours, // contextで提供
    loading,
    error,
    refreshSettings: fetchSettings,
  };


  if (loading && !settings) {
    return <div>システム設定を読み込んでいます...</div>;
  }

  return (
    <SystemSettingsContext.Provider
      value={contextValue}
    >
      {children}
    </SystemSettingsContext.Provider>
  );
};

export const useSystemSettings = () => {
  return useContext(SystemSettingsContext);
}; 